/**
 * Figures that can be unknown. Plan principle: "unknown is never displayed as zero".
 *
 *   fig(null, { reason: 'No open orders for US are in the ERP mirror.' })
 *     -> <span class="fig-unknown" title="…">—</span>
 *   fig(1234.5, { format: money('GBP') }) -> "£1,234.50"
 *
 * Returns HTML (escaped). Use it wherever a value comes from a source that can be
 * missing, stale or not covered, so the reason travels with the dash.
 */
import { escapeHtml } from '@isg/ui-kit';

export function fig(value, { format = (v) => v, reason = 'Not known' } = {}) {
  if (value === null || value === undefined || (typeof value === 'number' && Number.isNaN(value))) {
    return `<span class="fig-unknown" title="${escapeHtml(reason)}">—</span>`;
  }
  return escapeHtml(String(format(value)));
}

const SYMBOL = { GBP: '£', USD: '$', EUR: '€' };

export function money(currency, digits = 0) {
  return (v) => `${SYMBOL[currency] || (currency ? `${currency} ` : '')}${Number(v).toLocaleString('en-GB', {
    minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

export function count(v) {
  return Number(v).toLocaleString('en-GB');
}

/** '4 Sep' (or '4 Sep 2025' when not this year) for an ISO date. */
export function shortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  const opts = { day: 'numeric', month: 'short' };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString('en-GB', opts);
}

/** £78.1m, with the exact figure in the tooltip — a full figure overflows a tile. */
export function compactMoney(v, currency) {
  const sym = SYMBOL[currency] || (currency ? `${currency} ` : '');
  const a = Math.abs(v);
  if (a >= 1e6) return `${sym}${(v / 1e6).toFixed(1)}m`;
  if (a >= 1e4) return `${sym}${Math.round(v / 1e3)}k`;
  return `${sym}${Math.round(v).toLocaleString('en-GB')}`;
}
